import React, { useEffect, useState } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Users,
  Building2,
  Route,
  Calendar,
  Hotel,
  Plane,
  Tent,
  MapPin,
  ClipboardCheck,
  UserCheck,
  Globe2,
  Heart,
  Sparkles,
  Headphones,
  Languages,
  ChevronDown,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

type Props = {
  onStart: () => void;
};

export const LandingPage = ({ onStart }: Props) => {
  const { language, setLanguage, translations, direction } = useLanguage();
  
  const controls = useAnimation();
  const [scrollY, setScrollY] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Available languages
  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇦' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' }
    ];

  // Language Switcher Component
  const LanguageSwitcher = () => (
    <motion.div
      className={`fixed top-0 ${direction === 'rtl' ? 'right-0' : 'left-0'} w-full z-50`}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className={`flex ${direction === 'rtl' ? 'justify-start' : 'justify-end'}`}>
          <motion.div className="relative">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Languages size={18} className="text-[#00BFB3]" />
              <span className="text-sm font-medium text-gray-700">
                {languages.find(lang => lang.code === language)?.flag} {languages.find(lang => lang.code === language)?.label}
              </span>
              <ChevronDown
                size={16}
                className={`text-gray-500 transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </motion.button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
                >
                  {languages.map((lang) => (
                    <motion.button
                      key={lang.code}
                      whileHover={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
                      onClick={() => {
                        setLanguage(lang.code as 'ar' | 'en' | 'it' | 'fr');
                        setIsOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-sm text-left flex items-center gap-2
                        ${language === lang.code ? 'bg-primary-50 text-primary-600' : 'text-gray-700'}
                      `}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {language === lang.code && (
                        <motion.div
                          layoutId="activeIndicator"
                          className="w-1.5 h-1.5 rounded-full bg-primary-500 ml-auto"
                        />
                      )}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );

  // Custom geometric pattern animation
  const GeometricPattern = () => {
    const patternControls = useAnimation();

    useEffect(() => {
      const sequence = async () => {
        await patternControls.start({
          rotate: [0, 360],
          transition: { duration: 60, repeat: Infinity, ease: 'linear' },
        });
      };
      sequence();
    }, []);

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute left-1/2 top-1/2"
            animate={patternControls}
            style={{
              width: '400px',
              height: '400px',
              x: '-50%',
              y: '-50%',
              rotate: (360 / 8) * i,
            }}
          >
            <motion.div
              className="absolute w-full h-1 bg-gradient-to-r from-[#00BFB3]/0 via-[#00BFB3]/20 to-[#00BFB3]/0"
              style={{
                transformOrigin: 'center',
                rotate: (360 / 8) * i,
              }}
            />
          </motion.div>
        ))}
      </div>
    );
  };

  // Custom animated path for the pilgrim journey
  const PilgrimPath = () => {
    const pathControls = useAnimation();

    useEffect(() => {
      const animatePath = async () => {
        await pathControls.start({
          pathLength: [0, 1],
          transition: { duration: 2, ease: 'easeInOut' },
        });
      };
      animatePath();
    }, []);

    return (
      <motion.svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
      >
        <motion.path
          d="M10,50 Q30,20 50,50 T90,50"
          stroke="#00857B"
          strokeWidth="0.5"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={pathControls}
        />
      </motion.svg>
    );
  };

  // Animated mosque silhouette
  const MosqueSilhouette = () => (
    <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute bottom-0 left-0 w-full h-full"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 0.1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <div className="relative w-full h-full">
          {/* Mosque domes */}
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bottom-0 w-16 h-24 bg-[#00857B]"
              style={{
                left: `${15 + i * 20}%`,
                borderRadius: '50% 50% 0 0',
                opacity: 0.1,
              }}
              animate={{
                y: [0, -5, 0],
                transition: {
                  duration: 3,
                  delay: i * 0.2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );

  // Interactive particle system
  const ParticleSystem = () => {
    const particles = [...Array(20)].map((_, i) => ({
      id: i,
      size: Math.random() * 4 + 2,
      x: Math.random() * 100,
      y: Math.random() * 100,
    }));

    return (
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full bg-[#C5A572]"
            style={{
              width: particle.size,
              height: particle.size,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              opacity: 0.2,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
              opacity: [0.2, 0.5, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    );
  };

  // Scroll-based parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Floating animation for background elements
  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  // Wave animation for decorative elements
  const waveAnimation = {
    rotate: [-5, 5, -5],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  // Pulse animation
  const pulseAnimation = {
    scale: [1, 1.05, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  // Background shapes animation
  const BackgroundShapes = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating circles */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-[#00BFB3]/10"
        animate={floatingAnimation}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-[#C5A572]/10"
        animate={{
          ...floatingAnimation,
          transition: { ...floatingAnimation.transition, delay: 1 },
        }}
      />
      {/* Animated lines */}
      <motion.div
        className="absolute top-1/3 right-1/3 w-32 h-1 bg-[#00857B]/20 rounded-full"
        animate={waveAnimation}
      />
      <motion.div
        className="absolute bottom-1/3 left-1/3 w-32 h-1 bg-[#C5A572]/20 rounded-full"
        animate={{
          ...waveAnimation,
          transition: { ...waveAnimation.transition, delay: 0.5 },
        }}
      />
    </div>
  );

  const mainFeatures = [
    {
      key: 'accommodation',
      title: translations.landing.features.accommodation.title,
      description: translations.landing.features.accommodation.description,
      icon: Hotel,
      image:
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    },
    {
      key: 'movement',
      title: translations.landing.features.movement.title,
      description: translations.landing.features.movement.description,
      icon: Route,
      image:
        'https://images.unsplash.com/photo-1570125909232-eb263c188f7e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1471&q=80',
    },
    {
      key: 'camp',
      title: translations.landing.features.camp.title,
      description: translations.landing.features.camp.description,
      icon: Tent,
      image:
        'https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
    },
    {
      key: 'services',
      title: translations.landing.features.services.title,
      description: translations.landing.features.services.description,
      icon: Users,
      image:
        'https://images.unsplash.com/photo-1580418827493-f2b22c0a76cb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const systemBenefits = [
    {
      value: '95%',
      description: translations.landing.benefits.satisfaction,
      image:
        'https://images.unsplash.com/photo-1576267423048-15c0040fec78?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: Heart,
    },
    {
      value: '10K+',
      description: translations.landing.benefits.pilgrims,
      image:
        'https://images.unsplash.com/photo-1542816417-0983c9c9ad53?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: Users,
    },
    {
      value: '24/7',
      description: translations.landing.benefits.support,
      image:
        'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: Headphones,
    },
    {
      value: '100+',
      description: translations.landing.benefits.features,
      image:
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      icon: Sparkles,
    },
  ];

  return (
    <div className="min-h-screen bg-white" dir={direction}>
      {/* Language Switcher */}
      <LanguageSwitcher />

      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-[#00BFB3]/5 to-white overflow-hidden">
        <GeometricPattern />
        <ParticleSystem />
        <PilgrimPath />
        <MosqueSilhouette />
        <BackgroundShapes />
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 bg-[url('/images/pattern.png')] bg-repeat opacity-10"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            style={{
              y: scrollY * 0.5,
              opacity: 1 - scrollY * 0.002,
            }}
          >
            <div className="relative mb-16">
              <motion.div
                className="absolute -left-8 -top-8 w-16 h-16"
                animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              >
                <div className="w-full h-full border-4 border-[#00BFB3]/20 rounded-full" />
              </motion.div>

              <motion.img
                src="https://i.ibb.co/TBjqHtnV/logo-dark.png"
                alt="Ithraa Logo"
                className="h-32 relative z-10 mx-auto"
                initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                whileHover={{
                  scale: 1.05,
                  filter: 'brightness(1.1)',
                  transition: { duration: 0.3 },
                }}
              />
            </div>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <motion.h1
                className="text-5xl md:text-6xl font-bold text-[#00857B] mb-6"
                variants={itemVariants}
              >
                {translations?.landing?.hero?.title ||
                  'Integrated Hajj and Umrah Management System'}
                <br />
                <span className="text-[#C5A572]">
                  {translations?.landing?.hero?.subtitle || 'Comprehensive'}
                </span>
              </motion.h1>
              <motion.p
                className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto"
                variants={itemVariants}
              >
                {translations?.landing?.hero?.description ||
                  'Efficiently organize your operations through our integrated platform for accommodation, transportation, and pilgrim services management.'}
              </motion.p>
              <motion.button
                onClick={onStart}
                className="group relative inline-flex items-center gap-2 bg-[#00857B] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#00BFB3] transition-all duration-300"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {translations?.landing?.hero?.cta || 'Access System'}
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.div>
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Main Features */}
      <div className="relative py-20 bg-[#00BFB3]/5 overflow-hidden">
        <BackgroundShapes />
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            variants={fadeInUpVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {translations?.landing?.features?.title || 'Main Features'}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.key}
                variants={itemVariants}
                className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500"
                whileHover={{
                  y: -10,
                  transition: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                  },
                }}
              >
                <div className="relative h-48 overflow-hidden">
                  <motion.img
                    src={feature.image}
                    alt={
                      translations?.landing?.features?.[feature.key]?.title ||
                      feature.key
                    }
                    className="w-full h-full object-cover"
                    whileHover={{
                      scale: 1.15,
                      transition: { duration: 0.6 },
                    }}
                  />
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-[#00857B] to-transparent"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 0.9 }}
                  >
                    <motion.div
                      className="absolute bottom-4 left-4 flex items-center"
                      initial={{ x: -20, opacity: 0 }}
                      whileHover={{ x: 0, opacity: 1 }}
                    >
                      <feature.icon className="w-8 h-8 text-white mr-2" />
                      <span className="text-white font-semibold">
                        {translations?.landing?.features?.[feature.key]
                          ?.title || feature.key}
                      </span>
                    </motion.div>
                  </motion.div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {translations?.landing?.features?.[feature.key]?.title ||
                      feature.key}
                  </h3>
                  <p className="text-gray-600">
                    {translations?.landing?.features?.[feature.key]
                      ?.description || 'Description not available'}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* System Features */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            variants={fadeInUpVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {translations?.landing?.systemFeatures?.title ||
              'Integrated System Features'}
          </motion.h2>
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              {
                title:
                  translations?.landing?.systemFeatures?.hotel?.title ||
                  'Hotel Management',
                description:
                  translations?.landing?.systemFeatures?.hotel?.description ||
                  'Manage pilgrim accommodations efficiently.',
                icon: Building2,
              },
              {
                title:
                  translations?.landing?.systemFeatures?.flight?.title ||
                  'Flight Management',
                description:
                  translations?.landing?.systemFeatures?.flight?.description ||
                  'Organize flight schedules and bookings.',
                icon: Plane,
              },
              {
                title:
                  translations?.landing?.systemFeatures?.task?.title ||
                  'Task Management',
                description:
                  translations?.landing?.systemFeatures?.task?.description ||
                  'Track and manage tasks for pilgrims.',
                icon: ClipboardCheck,
              },
              {
                title:
                  translations?.landing?.systemFeatures?.guide?.title ||
                  'Guide Management',
                description:
                  translations?.landing?.systemFeatures?.guide?.description ||
                  'Assign and manage guides for pilgrims.',
                icon: UserCheck,
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={itemVariants}
                className="p-6 bg-white rounded-xl border border-gray-100 hover:border-[#C5A572] transition-colors"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#00BFB3]/10 to-[#00857B]/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#00857B]" />
                </div>
                <h3 className="text-lg font-semibold text-[#00857B] mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="relative bg-gradient-to-br from-[#00857B] to-[#00BFB3] text-white py-20 overflow-hidden">
        <BackgroundShapes />
        <motion.div
          className="absolute inset-0 bg-[url('/images/pattern.png')] bg-repeat opacity-5"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {translations?.landing?.benefits?.title || 'Key Benefits'}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {systemBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.description}
                variants={itemVariants}
                className="relative group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={index}
              >
                <motion.div
                  className="relative h-[300px] rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm"
                  whileHover={{ y: -10 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  {/* Background Image */}
                  <motion.div
                    className="absolute inset-0"
                    initial={{ scale: 1.2 }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <img
                      src={benefit.image}
                      alt={benefit.description}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#00857B]/90 to-transparent" />
                  </motion.div>

                  {/* Content */}
                  <div className="relative h-full flex flex-col items-center justify-end p-6 text-center">
                    <motion.div
                      className="mb-4 w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                    >
                      <benefit.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <motion.div
                      className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-white to-white/80"
                      animate={{
                        textShadow: [
                          '0 0 10px rgba(255,255,255,0.5)',
                          '0 0 20px rgba(255,255,255,0.3)',
                          '0 0 10px rgba(255,255,255,0.5)',
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {benefit.value}
                    </motion.div>
                    <div className="text-white/90 font-medium">
                      {benefit.description}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>


      {/* Final CTA */}
      <div className="py-20 bg-[#00BFB3]/5">
        <div className="container mx-auto px-4 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold mb-8 text-[#00857B]"
            variants={fadeInUpVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {translations?.landing?.finalCta?.title || 'Ready to Get Started?'}
          </motion.h2>
          <motion.p
            className="text-xl mb-12 text-gray-600 max-w-2xl mx-auto"
            variants={fadeInUpVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {translations?.landing?.finalCta?.description ||
              'Access your dashboard to manage accommodations, movements, and pilgrim services'}
          </motion.p>
          <motion.button
            onClick={onStart}
            className="group inline-flex items-center gap-2 bg-[#00857B] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#00BFB3] transition-all duration-300"
            variants={fadeInUpVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {translations?.landing?.finalCta?.cta || 'Access System'}
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight className="w-5 h-5" />
            </motion.div>
          </motion.button>
        </div>
      </div>

      {/* Footer with Rehab Company Copyright */}
      <footer className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center justify-center"
          >
            <div className="flex items-center gap-6 mb-6">
              <motion.img 
                src="https://i.ibb.co/jvPWN34G/rehab-logo-1-page-0001-1.png"
                alt="Rehab Company"
                className="h-20 object-contain"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-gray-600 text-center mb-2">
              {translations?.landing?.footer?.copyright || '© 2025 All rights reserved to Rehab Company'}
            </p>
            <p className="text-gray-500 text-center">
              {translations?.landing?.footer?.poweredBy || 'System developed and maintained by Rehab Company'}
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};
